import React, { useCallback, useReducer } from 'react';
import { useOAT } from 'common/providers/oatProvider';
import { socketMessages } from 'common/utils/socket';
import { SocketPayload } from '@azzurri-group/types';
import { Basket, BasketItem } from 'menus/interfaces';
import { useIfDefined } from 'common/hooks';
import BasketContext from './basket-context';
import basketReducer from './basket-reducer';
import buildInitialBasketState from './build-initial-basket-state';
import {
  ADD_ITEM,
  REMOVE_ITEM,
  UPDATE_BASKET,
  UPDATE_ITEM_REVIEW,
  UPDATE_SOCKET_BASKET,
} from './basket-actions';
import { basketPayloadReducer } from './basket-utils';

const { Provider } = BasketContext;

export interface BasketProviderProps {
  children: React.ReactNode;
}

interface BasketData {
  basket: Basket;
}

const BasketProvider = ({ children }: BasketProviderProps) => {
  const { socket, data } = useOAT<BasketData>();

  const { basket: initialBasket = {} } = data;
  const [basket, dispatch] = useReducer(basketReducer, buildInitialBasketState(initialBasket));

  const onBasketUpdate = useCallback(
    (basketData: Basket) =>
      socket.emit<SocketPayload.SubmitBasket>(socketMessages.basketUpdate, {
        basket: basketPayloadReducer(basketData),
      }),
    [socket]
  );

  const addItem = (payload: BasketItem) => {
    dispatch({
      type: ADD_ITEM,
      payload,
    });
  };

  const removeItem = (payload: BasketItem) => {
    dispatch({
      type: REMOVE_ITEM,
      payload,
    });
  };

  const updateItem = (payload: BasketItem) => {
    dispatch({
      type: UPDATE_ITEM_REVIEW,
      payload,
    });
  };

  const updateSocketBasket = () => {
    dispatch({ type: UPDATE_SOCKET_BASKET });
  };

  useIfDefined(() => {
    if (basket.updated) {
      onBasketUpdate(basket);
    }
  }, [basket]);

  useIfDefined(
    () => {
      dispatch({ type: UPDATE_BASKET, payload: data.basket });
    },
    data,
    'basket'
  );

  return (
    <Provider
      value={{
        basket,
        addItem,
        removeItem,
        updateItem,
        updateSocketBasket,
      }}
    >
      {children}
    </Provider>
  );
};

export default BasketProvider;
